/**
 * Validation Utilities
 *
 * Helper functions for common validation tasks across the application.
 * Provides reusable validation logic that doesn't fit into Zod schemas.
 *
 * @module lib/validation/utils
 */

import { z } from 'zod';

/**
 * Safe validate function that returns typed result
 * Wraps Zod's safeParse with better TypeScript inference
 *
 * @template T - The Zod schema type
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @returns Validation result with success flag and data/errors
 *
 * @example
 * const result = safeValidate(personDataSchema, formData);
 * if (result.success) {
 *   // result.data is typed as PersonData
 *   console.log(result.data.first_name);
 * } else {
 *   // result.errors is typed as ZodError
 *   console.error(result.errors);
 * }
 */
export function safeValidate<T extends z.ZodType>(
  schema: T,
  data: unknown
): { success: true; data: z.infer<T> } | { success: false; errors: z.ZodError } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, errors: result.error };
}

/**
 * Format Zod errors into user-friendly messages
 * Extracts the first error message for each field
 *
 * @param error - The ZodError to format
 * @returns Object mapping field paths to error messages
 *
 * @example
 * const errors = formatZodErrors(zodError);
 * // { first_name: "First name is required", date_of_death: "Death date must be after birth date" }
 */
export function formatZodErrors(error: z.ZodError): Record<string, string> {
  const formattedErrors: Record<string, string> = {};

  error.errors.forEach((err) => {
    const path = err.path.join('.');
    if (!formattedErrors[path]) {
      // Get the message, handling both string and function messages
      const message = typeof err.message === 'function' ? err.message() : err.message;
      formattedErrors[path] = message;
    }
  });

  return formattedErrors;
}

/**
 * Get the first error message from a ZodError
 * Useful for displaying a single error toast
 *
 * @param error - The ZodError to extract from
 * @returns The first error message or a generic fallback
 */
export function getFirstErrorMessage(error: z.ZodError): string {
  if (error.errors.length === 0) {
    return 'Validation failed';
  }

  const firstError = error.errors[0];
  const message = typeof firstError.message === 'function' ? firstError.message() : firstError.message;
  return message;
}

/**
 * Validate image file constraints
 * Standalone function for image validation (can be used with or without Zod)
 *
 * @param file - The file to validate
 * @param maxSizeMB - Maximum file size in megabytes (default: 5)
 * @returns Validation result with success flag and optional error message
 */
export function validateImageFile(
  file: File | null | undefined,
  maxSizeMB: number = 5
): { valid: true } | { valid: false; error: string } {
  if (!file) {
    return { valid: true }; // Null/undefined is valid (optional file)
  }

  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select a valid image file' };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { valid: false, error: `Image must be less than ${maxSizeMB}MB` };
  }

  return { valid: true };
}

/**
 * Validate date range (end date must be after start date)
 * Standalone function for date range validation
 *
 * @param startDate - The start date (e.g., birth date)
 * @param endDate - The end date (e.g., death date)
 * @returns True if valid or either date is missing, false if end < start
 */
export function validateDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): boolean {
  // If either date is missing, skip validation
  if (!startDate || !endDate) return true;

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Check if dates are valid
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

  return end >= start;
}

/**
 * Sanitize string input (trim and handle empty strings)
 * Converts empty strings to null for optional fields
 *
 * @param value - The string value to sanitize
 * @returns Trimmed string or null if empty
 */
export function sanitizeString(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Validate required string field
 * Checks that a string is non-empty after trimming
 *
 * @param value - The string value to validate
 * @param fieldName - The field name for error messages
 * @returns Validation result
 */
export function validateRequiredString(
  value: string | null | undefined,
  fieldName: string = 'Field'
): { valid: true; value: string } | { valid: false; error: string } {
  const sanitized = sanitizeString(value);

  if (!sanitized) {
    return { valid: false, error: `${fieldName} is required` };
  }

  return { valid: true, value: sanitized };
}
